import React, { useState, useEffect } from "react";
import AddBoxModal from "../AddBoxModal";
import { ConcorrentiService } from "../../services/concorrentiService";
import { Concorrente } from "../../types/concorrenti/concorrente";
import { GaraService } from "../../services/garaService";
import { Gara } from "../../types/gare/gara";

interface Props {
  gara: Gara;
}

const PartecipantiBoxTab: React.FC<Props> = ({ gara }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [boxes, setBoxes] = useState<Array<[string, string, string]>>([]);
  const [concorrentiDisponibili, setConcorrentiDisponibili] = useState<
    Concorrente[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false); // nuovo stato
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    const loadConcorrenti = async () => {
      setLoading(true);
      try {
        const data = await ConcorrentiService.getConcorrentiList(1, 1000, "", undefined, undefined, gara.discipline || 1);
        setConcorrentiDisponibili(data.results);
      } catch (err) {
        console.error("Errore nel caricamento concorrenti:", err);
      } finally {
        setLoading(false);
      }
    };
    const loadGroups = async () => {
      setLoadingGroups(true); // start loader gruppi
      try {
        const groups = await GaraService.getGroups(gara.id);
        // groups è un array di oggetti { groupId, competitorIds: number[] }
        // Convertiamo in Array<[string, string]>
        const loadedPairs: Array<[string, string, string]> = [];
        groups.forEach((group) => {
          if (group.competitors.length === 3) {
            loadedPairs.push([
              String(group.competitors[0].id),
              String(group.competitors[1].id),
              String(group.competitors[2].id),
            ]);
          }
        });
        setBoxes(loadedPairs);
      } catch (err) {
        console.error("Errore nel caricamento delle coppie:", err);
      } finally {
        setLoadingGroups(false); // stop loader gruppi
      }
    };

    loadConcorrenti();
    loadGroups();
  }, [gara.id, gara.discipline]);

  const handleAddBox = async (box: [string, string, string]) => {
    setBoxes((prev) => [...prev, box]);
    try {
      await GaraService.createGroup({
        competitionId: gara.id,
        competitorIds: [Number(box[0]), Number(box[1]), Number(box[2])],
      });
    } catch (err) {
      console.error("Errore nella creazione della coppia:", err);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Box da 3</h2>
      <button
        className="mb-4 px-4 py-2 bg-brand-600 text-white rounded hover:bg-brand-700"
        onClick={() => setModalOpen(true)}
      >
        Aggiungi Box
      </button>

      {/* Escludi concorrenti già presenti in un box */}
      <AddBoxModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        members={concorrentiDisponibili
          .filter((c) => !boxes.flat().includes(String(c.id)))
          .map((c) => ({
            id: String(c.id),
            name: `${c.lastName} ${c.firstName}`,
          }))}
        onAddBox={handleAddBox}
      />

      <div className="mt-6">
        <h3 className="text-lg font-semibold mb-2">Box creati:</h3>
        {loading || loadingGroups ? (
          <p className="text-gray-400 mt-2">Caricamento box...</p>
        ) : boxes.length === 0 ? (
          <p className="text-gray-500">Nessun box aggiunto.</p>
        ) : (
          <div className="space-y-3">
            {boxes.map(([id1, id2, id3], idx) => {
              const m1 = concorrentiDisponibili.find(
                (m) => String(m.id) === id1
              );
              const m2 = concorrentiDisponibili.find(
                (m) => String(m.id) === id2
              );
              const m3 = concorrentiDisponibili.find(
                (m) => String(m.id) === id3
              );
              const handleRemove = async () => {
                try {
                  // Passa anche la lista di competitorsId (id1, id2)
                  await GaraService.deleteGroup(gara.id, [
                    Number(id1),
                    Number(id2),
                    Number(id3),
                  ]);
                  setBoxes((prev) => prev.filter((p, i) => i !== idx));
                } catch (err: any) {
                    if (err?.status === 409) {
                      setRemoveError("Impossibile rimuovere il box: esistono dati collegati (turni, settori o classifica). Rimuovi prima i dati associati.");
                    } else {
                      setRemoveError("Errore nella rimozione del box.");
                    }
                    console.error("Errore nella rimozione del box:", err);
                  }
              };
              return (
                <div
                  key={idx}
                  className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {m1 ? `${m1.lastName} ${m1.firstName}` : id1} &ndash;{" "}
                          {m2 ? `${m2.lastName} ${m2.firstName}` : id2} &ndash;{" "}
                          {m3 ? `${m3.lastName} ${m3.firstName}` : id3}
                        </h4>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleRemove}
                    className="text-red-500 hover:text-red-700 font-medium text-sm transition-colors"
                  >
                    Rimuovi
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
            {removeError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setRemoveError(null)} />
          <div className="relative bg-white rounded-lg border border-brand-200 p-6 max-w-sm mx-auto">
            <h3 className="text-lg font-bold mb-2 text-red-600">Errore</h3>
            <p className="mb-4 text-gray-700">{removeError}</p>
            <button
              className="bg-brand-700 hover:bg-brand-600 text-white px-4 py-2 rounded"
              onClick={() => setRemoveError(null)}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
      {loading && (
        <p className="text-gray-400 mt-2">Caricamento concorrenti...</p>
      )}
    </div>
  );
};

export default PartecipantiBoxTab;
